import { useState, useContext } from "react";
import { CUSTOMER_PIN_LENGTH, CUSTOMER_PIN_MAX_ENTRY_LENGTH, isWeakPin, WEAK_PIN_MESSAGE } from "@shared/pinPolicy";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { apiRequest } from "@/lib/queryClient";
import freshorderLogo from "../assets/freshorder-logo.png";
import { ArrowLeft, Mail, Phone as PhoneIcon, Loader2, KeyRound, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { isNativeApp } from "@/lib/native/platform";
import { AccountRequestDialog } from "@/components/account-request-dialog";
import { AppStoreBadge } from "@/components/app-store-badge";
import { PinSetupCodeStep, PIN_CODE_ERROR_CODES, parseApiError } from "@/components/pin-setup-code-step";
// After sign-in: back to the order summary (?returnTo=) or on to one order (?order=).
import { afterSignInPath } from "@/lib/orderSummaryLinks";
import { chosenChildAccountId, lookupCustomer, type LookupAccountOption } from "@/lib/customerLookup";
import { AccountPickerStep } from "@/components/account-picker-step";

type LoginStep = "phone" | "pick-account" | "pin" | "forgot-pin" | "magic-sent" | "verify-code" | "set-pin" | "choose-account";

interface ChildAccount {
  id: number;
  name: string;
  customUrl: string;
}

interface AccountInfo {
  id: number;
  name: string;
  customUrl: string;
  hasPin: boolean;
  requiresPinSetup: boolean;
  hasEmail: boolean;
  hasPhone: boolean;
  isGroupParent?: boolean;
  childAccounts?: ChildAccount[];
  pinTarget?: { type: 'account' | 'contact'; id: number };
}

export default function PhoneEntry() {
  const [phone, setPhone] = useState("");
  const [longPin, setLongPin] = useState(false);
  const [pin, setPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [step, setStep] = useState<LoginStep>("phone");
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkMethod, setMagicLinkMethod] = useState<"email" | "sms" | null>(null);
  const [childAccounts, setChildAccounts] = useState<ChildAccount[]>([]);
  const [verifiedPin, setVerifiedPin] = useState("");
  const [contactId, setContactId] = useState<number | null>(null);
  // A phone on more than one account: the options the buyer picks from, and
  // the one they picked (a group location signs straight into that location).
  const [accountOptions, setAccountOptions] = useState<LookupAccountOption[]>([]);
  const [chosenOption, setChosenOption] = useState<LookupAccountOption | null>(null);
  const [requestOpen, setRequestOpen] = useState(false);
  // DS-1: the one-time code that proves this buyer holds the phone on file. It
  // is checked at the code step and sent again with the PIN.
  const [setupCode, setSetupCode] = useState("");
  const [codeStepError, setCodeStepError] = useState<string | null>(null);
  // A number this vendor has no account for: said under the field, where the
  // buyer is looking, rather than in a toast that disappears.
  const [notFound, setNotFound] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext?.organization;
  const orgSlug = orgContext?.orgSlug;

  const logoSrc = organization?.logoUrl || freshorderLogo;
  const logoAlt = organization?.name || "FreshOrder";

  // Where the card goes once it knows which account this is.
  const continueWithAccount = (account: AccountInfo) => {
    setAccountInfo(account);

    // Track contact ID for authorized contact auth flow
    if (account.pinTarget?.type === 'contact') {
      setContactId(account.pinTarget.id);
    } else {
      setContactId(null);
    }

    if (account.requiresPinSetup || !account.hasPin) {
      // DS-1: a first PIN needs proof that the buyer holds the phone on
      // file — a texted code, confirmed before the PIN step. A group
      // parent's session reaches every sub-account, so it goes through the
      // emailed/texted link instead.
      setSetupCode("");
      setCodeStepError(null);
      setStep(account.isGroupParent ? "forgot-pin" : "verify-code");
    } else {
      setStep("pin");
    }
  };

  const showLookupFailure = (kind: "not-found" | "rate-limited") => {
    if (kind === "rate-limited") {
      toast({
        title: "Too many attempts",
        description: "Please wait a while and try again.",
        variant: "destructive"
      });
      return;
    }
    setNotFound(true);
  };

  const handlePhoneLookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;

    setIsLoading(true);
    setNotFound(false);
    try {
      const result = await lookupCustomer<AccountInfo>(phone.replace(/\D/g, ''));
      setChosenOption(null);
      if (result.kind === "choose") {
        setAccountOptions(result.options);
        setStep("pick-account");
      } else if (result.kind === "account") {
        continueWithAccount(result.account);
      } else {
        showLookupFailure(result.kind);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to look up account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  // The server re-checks the choice against this phone's accounts and answers
  // with that one account, exactly as a phone on one account would.
  const handlePickAccount = async (option: LookupAccountOption) => {
    setIsLoading(true);
    try {
      const result = await lookupCustomer<AccountInfo>(phone.replace(/\D/g, ''), option);
      if (result.kind === "account") {
        setChosenOption(option);
        continueWithAccount(result.account);
      } else {
        showLookupFailure(result.kind === "rate-limited" ? "rate-limited" : "not-found");
        resetToPhone();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to look up account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handlePinSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin || pin.length < 4 || !accountInfo) return;

    setIsLoading(true);
    try {
      const childAccountId = chosenChildAccountId(chosenOption);
      const response = await apiRequest("POST", `/api/customer/login`, {
        accountId: accountInfo.id,
        pin,
        ...(contactId !== null ? { contactId } : {}),
        ...(childAccountId !== undefined ? { childAccountId } : {}),
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.requiresAccountSelection && data.childAccounts) {
          setChildAccounts(data.childAccounts);
          setVerifiedPin(pin);
          setStep("choose-account");
          setIsLoading(false);
          return;
        }
        
        const { customUrl } = data;
        const redirectPath = orgSlug
          ? afterSignInPath(orgSlug, customUrl, window.location.search)
          : `/${customUrl}`;
        setLocation(redirectPath);
      } else {
        const error = await response.json();
        toast({
          title: "Incorrect PIN",
          description: error.message || "Please check your PIN and try again.",
          variant: "destructive"
        });
        setPin("");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to verify PIN. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleAccountSelection = async (childAccount: ChildAccount) => {
    if (!accountInfo) return;
    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/customer/login`, {
        accountId: accountInfo.id,
        pin: verifiedPin,
        childAccountId: childAccount.id
      });
      
      if (response.ok) {
        const { customUrl } = await response.json();
        const redirectPath = orgSlug
          ? afterSignInPath(orgSlug, customUrl, window.location.search)
          : `/${customUrl}`;
        setLocation(redirectPath);
      } else {
        toast({
          title: "Error",
          description: "Unable to access this account. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to select account. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const handleSetPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPin || newPin.length !== CUSTOMER_PIN_LENGTH || newPin !== confirmPin || isWeakPin(newPin) || !accountInfo) return;

    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/customer/set-pin`, {
        accountId: accountInfo.id,
        pin: newPin,
        // DS-1: a first-time PIN set must name the phone this account was
        // looked up by, and carry the code that was sent to it.
        phone: phone.replace(/\D/g, ''),
        code: setupCode,
        ...(contactId !== null ? { contactId } : {}),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast({
          title: "PIN created!",
          description: "Your account is now secured with a PIN.",
        });
        
        if (accountInfo.isGroupParent && accountInfo.childAccounts && accountInfo.childAccounts.length > 0) {
          setChildAccounts(accountInfo.childAccounts);
          setVerifiedPin(newPin);
          setStep("choose-account");
          setIsLoading(false);
          return;
        }
        
        const redirectPath = orgSlug
          ? afterSignInPath(orgSlug, data.customUrl, window.location.search)
          : `/${data.customUrl}`;
        setLocation(redirectPath);
      } else {
        const error = await response.json();
        toast({
          title: "Error setting PIN",
          description: error.message || "Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      // apiRequest throws "<status>: <body>" on any non-2xx, so the server's
      // own message (wrong phone, too many attempts) has to be dug out here.
      const parsed = parseApiError(error);
      if (parsed.code && (PIN_CODE_ERROR_CODES as readonly string[]).includes(parsed.code)) {
        // The code went stale while the buyer was choosing a PIN. Back to the
        // code step, which sends a fresh one; the PIN they typed is kept.
        setSetupCode("");
        setCodeStepError(parsed.message || "That code is no longer valid. We've sent you a new one.");
        setStep("verify-code");
      } else {
        toast({
          title: "Error",
          description: parsed.message || "Unable to set PIN. Please try again.",
          variant: "destructive"
        });
      }
    }
    setIsLoading(false);
  };

  const handleForgotPin = async (method: "email" | "sms") => {
    if (!accountInfo) return;

    setIsLoading(true);
    try {
      const response = await apiRequest("POST", `/api/customer/send-magic-link`, {
        accountId: accountInfo.id,
        method
      });
      
      if (response.ok) {
        setMagicLinkMethod(method);
        setStep("magic-sent");
      } else {
        const error = await response.json();
        toast({
          title: "Unable to send reset link",
          description: error.message || "Please try again or contact support.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to send reset link. Please try again.",
        variant: "destructive"
      });
    }
    setIsLoading(false);
  };

  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    
    if (digits.length <= 7) {
      return digits.replace(/(\d{3})(\d{1,4})/, '$1-$2');
    }
    
    return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
    setNotFound(false);
  };

  const resetToPhone = () => {
    setStep("phone");
    setPin("");
    setNewPin("");
    setConfirmPin("");
    setAccountInfo(null);
    setChildAccounts([]);
    setVerifiedPin("");
    setContactId(null);
    setAccountOptions([]);
    setChosenOption(null);
    setSetupCode("");
    setCodeStepError(null);
  };

  const contactLink = organization?.companyEmail
    ? `mailto:${organization.companyEmail}`
    : "https://freshorder.io#contact";

  // A buyer who found this vendor by name in the app but has no account yet asks
  // for one in-app (BRIEF-account-request-flow) — this replaces the PR #122
  // "contact <vendor>" mailto/tel stopgap. Demo orgs never get the form (demo
  // invariants forbid email + SMS, and the server 404s the endpoint for them);
  // they fall back to the plain contact line.
  const vendorName = organization?.name || "your supplier";
  const canRequestAccount = !!organization && !organization.isDemo;
  const vendorEmail = organization?.companyEmail?.trim() || null;
  const vendorPhone = organization?.companyPhone?.trim() || null;
  const vendorTel = vendorPhone ? `tel:${vendorPhone.replace(/[^\d+]/g, "")}` : null;

  const adminPath = orgSlug ? `/org/${orgSlug}/admin` : "/admin";

  return (
    <div className="relative min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
      {/* Native shell only (TestFlight 2026-09-10): the sign-in card is a dead end —
          no way back to the launch screen to pick another vendor short of relaunching
          the app. iOS-style back affordance, sat below the status bar / Dynamic Island. */}
      {isNativeApp() && (
        <button
          type="button"
          onClick={() => setLocation("/app")}
          className="absolute left-2 z-10 flex items-center gap-0.5 rounded-lg py-1.5 pl-1 pr-2.5 text-sm font-medium text-[#2B5397] active:bg-gray-200 transition-colors"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
          data-testid="link-back-to-vendors"
        >
          <ChevronLeft className="h-5 w-5" />
          Your vendors
        </button>
      )}

      <Card className="w-full max-w-md shadow-lg border border-gray-200">
        <CardHeader className="text-center border-b border-gray-100 bg-gray-50/50 p-6">
          <img 
            src={logoSrc} 
            alt={logoAlt} 
            className="h-14 mx-auto mb-4 object-contain"
            data-testid="img-org-logo"
          />
          {step === "phone" && (
            <p className="text-sm text-gray-600">
              Enter the phone number {organization?.name || "your supplier"} has for you
            </p>
          )}
          {step === "pin" && accountInfo && (
            <div>
              <p className="font-medium text-gray-900">{accountInfo.name}</p>
              <p className="text-sm text-gray-600">Enter your PIN to continue</p>
            </div>
          )}
          {step === "verify-code" && accountInfo && (
            <div>
              <p className="font-medium text-gray-900">Welcome, {accountInfo.name}!</p>
              <p className="text-sm text-gray-600">First, let's confirm this is your phone</p>
            </div>
          )}
          {step === "set-pin" && accountInfo && (
            <div>
              <p className="font-medium text-gray-900">Welcome, {accountInfo.name}!</p>
              <p className="text-sm text-gray-600">Create a {CUSTOMER_PIN_LENGTH}-digit PIN to secure your account</p>
            </div>
          )}
          {step === "forgot-pin" && (
            <div>
              <p className="text-sm text-gray-600">
                {accountInfo && !accountInfo.hasPin
                  ? "We'll send you a link to set up your PIN"
                  : "We'll send you a link to reset your PIN"}
              </p>
            </div>
          )}
          {step === "magic-sent" && (
            <div>
              <p className="text-sm text-gray-600">Check your {magicLinkMethod === "email" ? "email" : "phone"} for a reset link</p>
            </div>
          )}
          {step === "choose-account" && accountInfo && (
            <div>
              <p className="font-medium text-gray-900">{accountInfo.name}</p>
              <p className="text-sm text-gray-600">Which account are you placing an order for?</p>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-6">
          {step === "phone" && (
            <form onSubmit={handlePhoneLookup} className="space-y-6">
              <div>
                <Input
                  type="tel"
                  placeholder="555-555-5555"
                  value={phone}
                  onChange={handlePhoneChange}
                  className="text-lg h-12 bg-[rgb(243,244,246)]"
                  maxLength={12}
                  autoFocus
                  aria-invalid={notFound || undefined}
                  aria-describedby={notFound ? "phone-not-found" : undefined}
                  data-testid="input-phone"
                />
                {notFound && (
                  <div id="phone-not-found" role="alert" className="mt-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-900" data-testid="text-phone-not-found">
                    <p className="font-medium">No account at {vendorName} has this number.</p>
                    <p className="mt-1">
                      Try your business's main number, or ask {vendorName} which number they have for you.
                      {canRequestAccount && " Still not found? Request an account below."}
                    </p>
                  </div>
                )}
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-[#2B5397] hover:bg-blue-100 hover:text-[#2B5397] text-white border border-[#2B5397] hover:border-blue-100"
                disabled={!phone.trim() || isLoading}
                data-testid="button-access-account"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </form>
          )}

          {step === "pin" && accountInfo && (
            <form onSubmit={handlePinSubmit} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  // PinInput renders one box per maxLength (InputOTPSlot is a no-op), so the
                  // count lives here: four boxes until the buyer says the PIN is longer.
                  maxLength={longPin || pin.length > CUSTOMER_PIN_LENGTH ? CUSTOMER_PIN_MAX_ENTRY_LENGTH : CUSTOMER_PIN_LENGTH}
                  value={pin}
                  onChange={setPin}
                  data-testid="input-pin"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {!longPin && pin.length <= CUSTOMER_PIN_LENGTH && (
                <button
                  type="button"
                  className="block mx-auto text-sm text-gray-500 underline"
                  onClick={() => setLongPin(true)}
                  data-testid="button-longer-pin"
                >
                  My PIN is longer than 4 digits
                </button>
              )}
              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-[#2B5397] hover:bg-blue-100 hover:text-[#2B5397] text-white border border-[#2B5397] hover:border-blue-100"
                disabled={pin.length < 4 || isLoading}
                data-testid="button-verify-pin"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5 mr-2" />
                    Unlock Account
                  </>
                )}
              </Button>
              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setStep("forgot-pin")}
                  className="text-sm text-[#2B5397] hover:underline"
                  data-testid="link-forgot-pin"
                >
                  Forgot your PIN?
                </button>
                <br />
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="text-sm text-gray-500 hover:underline inline-flex items-center"
                  data-testid="link-different-account"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Use a different phone number
                </button>
              </div>
            </form>
          )}

          {step === "pick-account" && (
            <AccountPickerStep
              options={accountOptions}
              onPick={handlePickAccount}
              onBack={resetToPhone}
              isLoading={isLoading}
            />
          )}

          {step === "choose-account" && (
            <div className="space-y-3">
              {childAccounts.map((child) => (
                <Button
                  key={child.id}
                  variant="outline"
                  className="w-full h-14 text-left justify-start text-base font-medium hover:bg-blue-50 hover:border-[#2B5397]"
                  onClick={() => handleAccountSelection(child)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 mr-3 animate-spin" />
                  ) : (
                    <ChevronRight className="h-5 w-5 mr-3 text-gray-400" />
                  )}
                  {child.name}
                </Button>
              ))}
              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="text-sm text-gray-500 hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Use a different phone number
                </button>
              </div>
            </div>
          )}

          {step === "verify-code" && accountInfo && (
            <PinSetupCodeStep
              accountId={accountInfo.id}
              contactId={contactId}
              phone={phone}
              vendorName={vendorName}
              // The emailed link sets the ACCOUNT's PIN, so it is no use to an
              // authorized contact setting up their own.
              canEmailLink={accountInfo.hasEmail && contactId === null}
              onEmailLink={() => handleForgotPin("email")}
              onVerified={(code) => {
                setSetupCode(code);
                setCodeStepError(null);
                setStep("set-pin");
              }}
              onBack={resetToPhone}
              backLabel="Use a different phone number"
              initialError={codeStepError}
              disabled={isLoading}
            />
          )}

          {step === "set-pin" && accountInfo && (
            <form onSubmit={handleSetPin} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Create PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={CUSTOMER_PIN_LENGTH}
                      value={newPin}
                      onChange={setNewPin}
                      data-testid="input-new-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirm PIN</label>
                  <div className="flex justify-center">
                    <InputOTP
                      maxLength={CUSTOMER_PIN_LENGTH}
                      value={confirmPin}
                      onChange={setConfirmPin}
                      data-testid="input-confirm-pin"
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                  {confirmPin.length === CUSTOMER_PIN_LENGTH && newPin !== confirmPin && (
                    <p className="text-red-500 text-sm text-center mt-2">PINs don't match</p>
                  )}
                  {newPin.length === CUSTOMER_PIN_LENGTH && isWeakPin(newPin) && (
                    <p className="text-red-500 text-sm text-center mt-2" data-testid="text-weak-pin">{WEAK_PIN_MESSAGE}</p>
                  )}
                </div>
              </div>
              <Button 
                type="submit" 
                className="w-full h-12 text-lg bg-[#3D8C40] hover:bg-green-600 text-white"
                disabled={newPin.length !== CUSTOMER_PIN_LENGTH || newPin !== confirmPin || isWeakPin(newPin) || isLoading}
                data-testid="button-set-pin"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    <KeyRound className="h-5 w-5 mr-2" />
                    Create PIN & Continue
                  </>
                )}
              </Button>
              <div className="text-center">
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="text-sm text-gray-500 hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Use a different phone number
                </button>
              </div>
            </form>
          )}

          {step === "forgot-pin" && accountInfo && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Choose how you'd like to receive {accountInfo.hasPin ? "a reset" : "your"} link:
              </p>
              
              {accountInfo.hasEmail && (
                <Button
                  onClick={() => handleForgotPin("email")}
                  variant="outline"
                  className="w-full h-12 justify-start"
                  disabled={isLoading}
                  data-testid="button-reset-via-email"
                >
                  <Mail className="h-5 w-5 mr-3" />
                  Send {accountInfo.hasPin ? "reset link" : "link"} via Email
                </Button>
              )}
              
              {accountInfo.hasPhone && (
                <Button
                  onClick={() => handleForgotPin("sms")}
                  variant="outline"
                  className="w-full h-12 justify-start"
                  disabled={isLoading}
                  data-testid="button-reset-via-sms"
                >
                  <PhoneIcon className="h-5 w-5 mr-3" />
                  Send {accountInfo.hasPin ? "reset link" : "link"} via SMS
                </Button>
              )}

              {!accountInfo.hasEmail && !accountInfo.hasPhone && (
                <div className="text-center text-sm text-gray-500">
                  <p>No contact method available on file.</p>
                  <p className="mt-2">Please contact support for help.</p>
                </div>
              )}

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => (accountInfo.hasPin ? setStep("pin") : resetToPhone())}
                  className="text-sm text-gray-500 hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  {accountInfo.hasPin ? "Back to PIN entry" : "Use a different phone number"}
                </button>
              </div>
            </div>
          )}

          {step === "magic-sent" && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                {magicLinkMethod === "email" ? (
                  <Mail className="h-8 w-8 text-green-600" />
                ) : (
                  <PhoneIcon className="h-8 w-8 text-green-600" />
                )}
              </div>
              <p className="text-gray-600">
                We've sent a reset link to your {magicLinkMethod === "email" ? "email" : "phone"}.
              </p>
              <p className="text-sm text-gray-500">
                The link will expire in 15 minutes.
              </p>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={resetToPhone}
                  className="text-sm text-[#2B5397] hover:underline"
                >
                  Start over
                </button>
              </div>
            </div>
          )}
          
          {step === "phone" && (
            <div className="mt-6 text-center space-y-2">
              {canRequestAccount && (
                <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 space-y-1" data-testid="block-request-account">
                  <p className="text-sm text-gray-600">
                    This app is for existing customers of {vendorName}.
                  </p>
                  <p className="text-sm text-gray-600">
                    Not a customer yet?{" "}
                    <button
                      type="button"
                      onClick={() => setRequestOpen(true)}
                      className="text-[#2B5397] hover:underline font-medium"
                      data-testid="button-request-account"
                    >
                      Request an account
                    </button>
                  </p>
                </div>
              )}
              {!canRequestAccount && (vendorEmail || vendorTel) && (
                <p className="text-sm text-gray-600" data-testid="text-not-a-customer">
                  Not a customer yet? Contact {organization?.name || "your supplier"}
                  {vendorEmail && (
                    <>
                      {" "}
                      <a href={`mailto:${vendorEmail}`} className="text-[#2B5397] hover:underline font-medium" data-testid="link-vendor-email">
                        {vendorEmail}
                      </a>
                    </>
                  )}
                  {vendorEmail && vendorTel && " or "}
                  {vendorTel && (
                    <a href={vendorTel} className="text-[#2B5397] hover:underline font-medium" data-testid="link-vendor-phone">
                      {vendorPhone}
                    </a>
                  )}
                </p>
              )}
              {!canRequestAccount && (
                <p className="text-sm text-gray-600">
                  Need help? <a href={contactLink} className="text-[#2B5397] hover:underline font-medium" target="_blank" rel="noopener noreferrer" data-testid="link-contact">Contact</a> us for support
                </p>
              )}
              {/* iPhone/iPad browsers only — demo orgs included. */}
              <AppStoreBadge />
              {/* Staff link is web-only. The iOS/Android shell is a
                  customer-facing app; surfacing an admin entry point there
                  invites App Review 4.2 / 2.1 questions about hidden
                  functionality. Admins sign in from a browser. */}
              {!isNativeApp() && (
                <p className="text-xs text-gray-500">
                  Staff access: <a href={adminPath} className="text-[#3D8C40] hover:underline" data-testid="link-admin">Admin Dashboard</a>
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {canRequestAccount && (
        <AccountRequestDialog
          open={requestOpen}
          onOpenChange={setRequestOpen}
          vendorName={vendorName}
          initialPhone={phone}
        />
      )}
    </div>
  );
}
